import PageLayout from "../../components/PageLayout";
import { useResponsive } from "../../lib/useResponsive";

const steps = [
  {
    n: "1",
    title: "Your investment date is recorded",
    desc: "When you wire funds, the exact date is logged as your entry anchor.",
  },
  {
    n: "2",
    title: "Previous day's closing value is pulled",
    desc: "SSN uses Charles Schwab's official closing balance from the day before your deposit.",
  },
  {
    n: "3",
    title: "Unit price is calculated",
    desc: "Portfolio value divided by units already sold = your entry price per unit.",
    formula: { label: "Unit Price Formula", text: "Unit Price = Portfolio Value ÷ Units Sold" },
  },
  {
    n: "4",
    title: "New units are issued to you",
    desc: "Your investment ÷ unit price = your units. SSN total units increase accordingly.",
    formula: { label: "Units Issued to You", text: "Your Units = Investment ÷ Unit Price" },
  },
  {
    n: "5",
    title: "Your value grows as SSN grows",
    desc: "Units stay fixed. As portfolio grows, unit price rises — so does your value.",
  },
];

const tableRows = [
  { label: "Date of investment", value: "June 10, 2026", hi: false },
  { label: "Account closing (June 9)", value: "$1,000,000", bold: true, hi: false },
  { label: "Units already sold", value: "1,000", bold: true, hi: false },
  { label: "Unit price today", value: "$1,000 / unit", bold: true, hi: true, green: true },
  { label: "You invest", value: "$25,000", bold: true, hi: false },
  { label: "New units issued to you", value: "25 units", bold: true, hi: false },
  { label: "New SSN total units", value: "1,025 units ✓", bold: true, hi: true, resRow: true, green: true },
];

export default function HowItWorksPage() {
  const { isMobile } = useResponsive();

  return (
    <PageLayout
      eyebrow="How It Works"
      title={<>You own <em>units</em>.<br />Units own <em>SSN Mutual Fund</em>.</>}
      subtitle=""
    >
      {/* Step Timeline */}
      <div style={{ ...S.stepFlow, marginBottom: isMobile ? 28 : 40 }}>
        {steps.map((step, i) => (
          <div key={step.n} style={S.stepItem}>
            <div style={S.leftColumn}>
              <div style={S.stepNum}>{step.n}</div>
              {i < steps.length - 1 && <div style={S.connector} />}
            </div>
            <div style={{ ...S.stepBody, paddingBottom: isMobile ? 20 : 28 }}>
              <strong style={{ ...S.stepTitle, fontSize: isMobile ? 14 : 16 }}>{step.title}</strong>
              <p style={S.stepDesc}>{step.desc}</p>
              {step.formula && (
                <div style={{ ...S.formulaCard, maxWidth: isMobile ? "100%" : 440 }}>
                  <div style={S.flabel}>{step.formula.label}</div>
                  <div style={{ ...S.ftext, fontSize: isMobile ? 12 : 13 }}>{step.formula.text}</div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Worked Example */}
      <div style={{ ...S.exampleCard, padding: isMobile ? "16px" : 24, marginBottom: isMobile ? 28 : 40 }}>
        <div style={S.etag}>📘 Worked Example — Joining SSN Mutual Fund</div>
        <h4 style={{ ...S.exampleTitle, fontSize: isMobile ? 14 : 16 }}>Step-by-step unit calculation</h4>

        <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch", marginBottom: 16 }}>
          <table style={{ ...S.miniT, minWidth: isMobile ? 300 : "auto" }}>
            <tbody>
              {tableRows.map((row, i) => (
                <tr key={i} style={row.resRow ? S.resRow : row.hi ? S.hiRow : {}}>
                  <td style={{ ...S.td, fontSize: isMobile ? 13 : 14 }}>{row.label}</td>
                  <td style={{
                    ...S.valText,
                    fontSize: isMobile ? 13 : 14,
                    ...(row.green ? S.highlightedGreen : {}),
                  }}>
                    {row.bold ? <strong>{row.value}</strong> : row.value}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ ...S.enote, fontSize: isMobile ? 12 : 13 }}>
          📈 Six months later — fund grows to $1.5M with 1,025 units → unit price = $1,463. Your 25 units now worth <strong>$36,585</strong>.
        </div>
      </div>
    </PageLayout>
  );
}

const S: Record<string, React.CSSProperties> = {
  stepFlow: { display: "flex", flexDirection: "column", marginBottom: 40 },
  stepItem: { display: "flex", gap: 16 },
  leftColumn: { display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0, width: 32 },
  stepNum: {
    width: 32, height: 32, borderRadius: "50%", fontSize: 13, fontWeight: 700,
    background: "rgba(29,158,117,0.15)", border: "1px solid rgba(29,158,117,0.3)",
    color: "var(--teal-light)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  connector: { flex: 1, width: 1, background: "rgba(29,158,117,0.2)", margin: "8px 0" },
  stepBody: { flex: 1, paddingBottom: 28, paddingTop: 4 },
  stepTitle: { display: "block", fontSize: 16, color: "var(--text-primary)", marginBottom: 6 },
  stepDesc: { fontSize: 14, color: "var(--text-secondary)", lineHeight: 1.6, margin: "0 0 4px 0" },
  formulaCard: {
    background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)",
    borderRadius: 8, padding: "12px 16px", marginTop: 12,
  },
  flabel: { fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-muted)", marginBottom: 4 },
  ftext: { fontSize: 13, fontFamily: "monospace", color: "var(--teal-light)", fontWeight: 600 },
  exampleCard: {
    background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 12, padding: 24, marginBottom: 40,
  },
  etag: { fontSize: 13, color: "var(--teal-light)", fontWeight: 500, marginBottom: 8 },
  exampleTitle: { fontSize: 16, color: "var(--text-primary)", fontWeight: 600, marginBottom: 16 },
  miniT: { width: "100%", borderCollapse: "collapse", fontSize: 14 },
  td: { padding: "10px 12px", borderBottom: "1px solid rgba(255,255,255,0.05)", color: "var(--text-secondary)", whiteSpace: "nowrap" },
  valText: { padding: "10px 12px", borderBottom: "1px solid rgba(255,255,255,0.05)", textAlign: "right", color: "var(--text-primary)", whiteSpace: "nowrap" },
  hiRow: { background: "rgba(29,158,117,0.03)" },
  resRow: { background: "rgba(29,158,117,0.06)" },
  highlightedGreen: { color: "var(--teal-light)" },
  enote: {
    fontSize: 13, color: "var(--text-secondary)", background: "rgba(255,255,255,0.02)",
    padding: 12, borderRadius: 8, borderLeft: "3px solid var(--teal-light)", lineHeight: 1.5,
  },
};
